import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { Command } from 'cmdk';
import {
  CalendarClock,
  CreditCard,
  HardDrive,
  History,
  LayoutDashboard,
  Package,
  Receipt,
  RefreshCw,
  Search,
  Settings,
  Shield,
  ShoppingCart,
  FileText,
  Users,
} from 'lucide-react';

interface NavItem {
  label: string;
  to: string;
  keywords: string[];
  icon: React.ElementType;
}

const navItems: NavItem[] = [
  { label: 'Dashboard', to: '/', keywords: ['home'], icon: LayoutDashboard },
  { label: 'Tenants', to: '/tenants', keywords: ['customers'], icon: Users },
  { label: 'Subscriptions', to: '/subscriptions', keywords: ['licenses'], icon: FileText },
  { label: 'Orders', to: '/orders', keywords: ['purchases'], icon: ShoppingCart },
  { label: 'Catalog', to: '/catalog', keywords: ['products'], icon: Package },
  { label: 'Invoices', to: '/billing/invoices', keywords: ['billing'], icon: Receipt },
  { label: 'Payments', to: '/billing/payments', keywords: ['transactions'], icon: CreditCard },
  { label: 'Security', to: '/security', keywords: ['alerts', 'threats'], icon: Shield },
  { label: 'Backups', to: '/backups', keywords: ['acronis', 'recovery'], icon: HardDrive },
  { label: 'Activity', to: '/activity', keywords: ['history', 'audit'], icon: History },
  { label: 'Reconciliation', to: '/reconciliation', keywords: ['sync', 'mismatches'], icon: RefreshCw },
  { label: 'Renewals', to: '/renewals', keywords: ['expiring', 'subscriptions'], icon: CalendarClock },
  { label: 'Settings', to: '/settings', keywords: ['configuration'], icon: Settings },
];

const DIALOG_CONTENT_CLASS = [
  'fixed left-1/2 top-[20%] z-50 w-full max-w-lg -translate-x-1/2',
  'rounded-xl border bg-popover text-popover-foreground shadow-lg',
  'animate-in fade-in-0 zoom-in-95',
].join(' ');

const INPUT_CLASS = [
  'flex h-11 w-full bg-transparent py-3 text-sm',
  'placeholder:text-muted-foreground',
  'outline-none disabled:cursor-not-allowed disabled:opacity-50',
].join(' ');

const ITEM_CLASS = [
  'relative flex cursor-pointer select-none items-center rounded-md px-2 py-2',
  'text-sm outline-none',
  'data-[selected=true]:bg-accent data-[selected=true]:text-accent-foreground',
  'hover:bg-accent hover:text-accent-foreground',
].join(' ');

const GROUP_CLASS = [
  '[&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:py-1.5',
  '[&_[cmdk-group-heading]]:text-xs [&_[cmdk-group-heading]]:font-medium',
  '[&_[cmdk-group-heading]]:text-muted-foreground',
].join(' ');

function useCommandPaletteShortcut(onToggle: () => void) {
  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'k' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        onToggle();
      }
    }

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onToggle]);
}

function SearchInput() {
  return (
    <div className="flex items-center gap-2 border-b px-3">
      <Search className="h-4 w-4 shrink-0 text-muted-foreground" />
      <Command.Input placeholder="Type a command or search..." className={INPUT_CLASS} />
    </div>
  );
}

function NavItemRow({ item }: { item: NavItem }) {
  const Icon = item.icon;
  return (
    <div className="flex items-center gap-3">
      <Icon className="h-4 w-4 shrink-0 text-muted-foreground" />
      <span>{item.label}</span>
    </div>
  );
}

function NavigationGroup({ onSelect }: { onSelect: (to: string) => void }) {
  return (
    <Command.List className="max-h-[300px] overflow-y-auto p-2">
      <Command.Empty className="py-6 text-center text-sm text-muted-foreground">
        No results found.
      </Command.Empty>
      <Command.Group heading="Navigation" className={GROUP_CLASS}>
        {navItems.map((item) => (
          <Command.Item
            key={item.to}
            value={item.label}
            keywords={item.keywords}
            onSelect={() => onSelect(item.to)}
            className={ITEM_CLASS}
          >
            <NavItemRow item={item} />
          </Command.Item>
        ))}
      </Command.Group>
    </Command.List>
  );
}

function PaletteFooter() {
  return (
    <div className="flex items-center justify-between border-t px-3 py-2 text-xs text-muted-foreground">
      <span>Navigate with arrow keys</span>
      <div className="flex gap-1">
        <kbd className="rounded border bg-muted px-1.5 py-0.5 font-mono text-[10px]">Enter</kbd>
        <span>to select</span>
      </div>
    </div>
  );
}

export function CommandPalette() {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  const toggle = useCallback(() => setOpen((prev) => !prev), []);
  useCommandPaletteShortcut(toggle);

  const handleSelect = useCallback(
    (to: string) => {
      setOpen(false);
      navigate({ to });
    },
    [navigate],
  );

  return (
    <Command.Dialog
      open={open}
      onOpenChange={setOpen}
      label="Command palette"
      overlayClassName="fixed inset-0 z-50 bg-black/50"
      contentClassName={DIALOG_CONTENT_CLASS}
      loop
    >
      <SearchInput />
      <NavigationGroup onSelect={handleSelect} />
      <PaletteFooter />
    </Command.Dialog>
  );
}
